import { PreferenceDefaults, PreferenceName } from '@gandalf/constants';
import { inject, InjectionToken } from '@angular/core';
import { SecurityManagerService } from '@core/security-manager/security-manager.service';
import { EnumClass, EnumUtil } from 'morgana';
import { GandalfConstant } from 'gandalf';
import { _isNil } from '@core/lodash/lodash';

const preferenceTokensMap = new Map<PreferenceName, InjectionToken<any>>();

const preferenceDefaults = {
	[PreferenceName.AI_SCRIBE_ENABLED.value]: PreferenceDefaults.AI_SCRIBE_ENABLED,
	[PreferenceName.ACCOUNTING_STATEMENTS_FORMAT.value]: PreferenceDefaults.ACCOUNTING_STATEMENTS_FORMAT,
};

function getToken<T>(preference: PreferenceName, factory: () => T): InjectionToken<T> {
	if (preferenceTokensMap.has(preference)) {
		return preferenceTokensMap.get(preference);
	}
	const token = new InjectionToken<T>(preference.label, {providedIn: 'root', factory});
	preferenceTokensMap.set(preference, token);
	return token;
}

function getPreferenceValue(preference: PreferenceName): string {
	return inject(SecurityManagerService).preferenceValue(preference.value) ?? preferenceDefaults[preference.value]?.value;
}

function getPreferenceNumber(preference: PreferenceName): number {
	const value = getPreferenceValue(preference);
	return !_isNil(value) ? Number(value) : undefined;
}

export function preferenceToken(preference: PreferenceName): InjectionToken<string> {
	return getToken(preference, () => getPreferenceValue(preference));
}

export function preferenceOnToken(preference: PreferenceName): InjectionToken<boolean> {
	return getToken(preference, () => ['true', 'yes', 'on'].includes(getPreferenceValue(preference)?.toLowerCase()));
}

export function preferenceNumberToken(preference: PreferenceName): InjectionToken<number> {
	return getToken(preference, () => getPreferenceNumber(preference));
}

export function preferenceEnumToken<T extends GandalfConstant<number>>(preference: PreferenceName, enumClass: EnumClass<T>): InjectionToken<T> {
	return getToken(preference, () => EnumUtil.findEnumByValue(getPreferenceNumber(preference), enumClass));
}

export function preferenceEnumStringToken<T extends GandalfConstant<string>>(preference: PreferenceName, enumClass: EnumClass<T>): InjectionToken<T> {
	return getToken(preference, () => EnumUtil.findEnumByValue(getPreferenceValue(preference), enumClass));
}
